import {Form as AntForm} from 'antd';
import React, {useEffect} from 'react';

import Form from '../../../../components/Form/Form';
import MobileModalForm from '../../../../components/Form/MobileModalForm';
import DeviceGroupingTypes from '../../../../consts/devices/deviceGroupingTypes.ts';
import * as formInputNames from '../../../../consts/form/formInputNames';
import * as localizationKeys from '../../../../consts/localization/localizationKeys';
import {reportFormats} from '../../../../consts/reports/reportFormats.ts';
import {reportTypes} from '../../../../consts/reports/reportTypes.ts';
import useIsMobileViewport from '../../../../hooks/useIsMobileViewport';
import buildingsService from '../../../../services/buildings/buildingsService';
import deviceService from '../../../../services/device/deviceService';
import {getLocalizedStrings} from '../../../../services/localization/localizationService';
import log from '../../../../services/logger/log';
import organizationsService from '../../../../services/organizations/organizationsService';
import {setBuildingsOrganizationId} from '../../../../state/ducks/buildings';
import {dispatch} from '../../../../state/store';
import {utcEndOfDay, utcStartOfDay} from '../../../../utils/dateUtils';
import helpers from '../../../../utils/helpers';
import reportsModalService from './reportsModalService';
const ReportsModal = (props) => {
    const {onOk, onCancel} = props;
    const [form] = AntForm.useForm();
    const isMobile = useIsMobileViewport();
    const localizedStrings = getLocalizedStrings();

    const modalData = reportsModalService.getModalContent();

    const {formContent, title, submitButton, cancelButton, initialValues, requireDeps} = modalData || {};

    useEffect(() => {
        document.body.classList.add('reports-modal-open');

        return () => document.body.classList.remove('reports-modal-open');
    }, []);

    const onFinish = async () => {
        try {
            const values = await form.validateFields();

            helpers.runFunction(onOk, values);
        } catch (error) {
            log.error(`ReportsModal - validate failed: ${error}`);
        }
    };

    const resetFormField = (fieldNames) => {
        const fieldsToReset = {};

        fieldNames.forEach((name) => (fieldsToReset[name] = null));
        form.setFieldsValue(fieldsToReset);
    };

    const setFormField = (name, key, value) => form.setFieldsValue({[name]: {id: key, value}});

    const handleDatePick = (date, name) => {
        if (!date) {
            form.setFieldsValue({[name]: null});
            return;
        }

        if (name === formInputNames.START_DATE) {
            form.setFieldsValue({[name]: utcStartOfDay(date)});
        } else {
            form.setFieldsValue({[name]: utcEndOfDay(date)});
        }
    };

    const handleRadioChange = (value, name) => form.setFieldsValue({[name]: value});

    const handleSelect = async (value, name, option) => {
        const currentValue = form.getFieldValue(name);

        if (currentValue?.value === option.value) return;

        setFormField(name, option.key, option.value);

        switch (name) {
            case formInputNames.REPORT_TYPE: {
                resetFormField([
                    formInputNames.DEVICE_ID,
                    formInputNames.DEVICE_TYPE,
                    formInputNames.END_DATE,
                    formInputNames.START_DATE,
                ]);
                break;
            }
            case formInputNames.CITY: {
                resetFormField([
                    formInputNames.COMPANY,
                    formInputNames.BUILDING,
                    formInputNames.DEVICE_ID,
                    formInputNames.DEVICE_TYPE,
                ]);
                await organizationsService.getOrganizationsNames(value);
                break;
            }
            case formInputNames.COMPANY: {
                resetFormField([formInputNames.BUILDING, formInputNames.DEVICE_ID, formInputNames.DEVICE_TYPE]);
                dispatch(setBuildingsOrganizationId(value));
                await buildingsService.getBuildingsListByOrganizationId();
                break;
            }
            case formInputNames.BUILDING: {
                resetFormField([formInputNames.DEVICE_TYPE, formInputNames.DEVICE_ID]);
                const buildingId = form.getFieldValue(formInputNames.BUILDING);

                await deviceService.getDeviceTypesGroupsByBuildingId(buildingId?.id);
                break;
            }
            case formInputNames.DEVICE_TYPE: {
                resetFormField([formInputNames.DEVICE_ID]);
                const currentReportId = form.getFieldValue(formInputNames.REPORT_TYPE)?.id;
                const isBuildingReport = currentReportId === reportTypes.Building;
                const isIndividualReport = currentReportId === reportTypes.Individual;
                const buildingId = form.getFieldValue(formInputNames.BUILDING);

                if (isBuildingReport) {
                    await deviceService.getDevices({
                        groupID: option?.key,
                        buildingId: buildingId?.id,
                        groupingType: DeviceGroupingTypes.Distributed,
                    });
                } else if (isIndividualReport) {
                    await deviceService.getDevices({groupID: option?.key, buildingId: buildingId?.id});
                }
                break;
            }
            default:
                break;
        }
    };

    const handleChange = () => {};

    if (isMobile) {
        return (
            <MobileModalForm
                form={form}
                formName='report-mobile-form'
                title={title}
                closeLabel={localizedStrings[localizationKeys.REPORTS_MOBILE_CLOSE_FORM]}
                formFields={formContent}
                initialValues={{[formInputNames.REPORT_FORMAT]: reportFormats.PDF, ...initialValues}}
                requireDeps={requireDeps}
                onFinish={onFinish}
                onCancel={onCancel}
                submitButton={submitButton}
                cancelButton={cancelButton}
                handleDatePick={handleDatePick}
                handleRadioChange={handleRadioChange}
                handleSelect={handleSelect}
                handleChange={handleChange}
            />
        );
    }

    return (
        <Form
            requireDeps={requireDeps}
            handleDatePick={handleDatePick}
            handleRadioChange={handleRadioChange}
            handleSelect={handleSelect}
            closeModal={onCancel}
            initialValues={initialValues}
            submitButton={submitButton}
            onFinish={onFinish}
            title={title}
            formFields={formContent}
            cancelButton={cancelButton}
            formInstance={form}
            handleChange={handleChange}
            formClassName='reports-form'
            containerClassName='reports-form-content'
            buttonsClassName='reports-form-actions'
        />
    );
};

export default React.memo(ReportsModal);
